import { Button, Input } from '@ibsys/kit';
import { PlusIcon } from 'lucide-react';
import { FC, KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  /** Отбор задан — без набранных записей выборка запомнит его */
  hasSelection: boolean;
  /** Сколько диалогов набрано — непустой набор делает выборку выборкой по набору записей */
  pickedCount: number;
  /** Идёт сохранение — повторное создание не запускается */
  isPending: boolean;
  onCreate: (name: string) => void;
};

/**
 * Сохранение текущего состояния списка как новой выборки: пользователь задаёт только название.
 * Что выборка запомнит — набранные записи или отбор, — определяет набранное; после создания это
 * не меняется, поэтому состав другого рода сохраняется отдельной выборкой.
 */
export const SampleCreateForm: FC<Props> = ({ hasSelection, pickedCount, isPending, onCreate }) => {
  const { t } = useTranslation(['common', 'samples']);
  const [name, setName] = useState('');

  // Сохранять нечего, пока не набраны записи и не задан отбор.
  const hasContent = pickedCount > 0 || hasSelection;
  const canCreate = Boolean(name.trim()) && hasContent && !isPending;

  const handleCreate = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed || !hasContent || isPending) return;
    onCreate(trimmed);
    setName('');
  }, [name, hasContent, isPending, onCreate]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCreate();
      }
    },
    [handleCreate],
  );

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <p className="text-xs text-muted-foreground">{t('samples:createHint')}</p>

      <p className="text-xs text-muted-foreground">
        {pickedCount > 0 ? t('samples:createRecordsHint', { count: pickedCount }) : t('samples:createFiltersHint')}
      </p>

      <div className="flex gap-2">
        <Input
          size="sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('samples:newSamplePlaceholder')}
          className="flex-1"
        />
        <Button
          variant="default"
          size="sm"
          icon={<PlusIcon className="size-4" />}
          disabled={!canCreate}
          onClick={handleCreate}
          aria-label={t('samples:create')}
        />
      </div>
    </div>
  );
};
